import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, Globe, X } from 'lucide-react';

const CATEGORIES = ['All', 'Medical', 'Daily Care', 'Maintenance', 'Conditioning', 'Stats', 'Travel', 'Notes'];

const FILTER_STYLES = {
  'All':          { active: 'bg-primary text-white border-primary',              inactive: 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50' },
  'Medical':      { active: 'bg-purple-100 text-purple-700 border-purple-300',        inactive: 'bg-purple-50 text-purple-500 border-purple-200 hover:bg-purple-100' },
  'Daily Care':   { active: 'bg-sky-100 text-sky-700 border-sky-300',                 inactive: 'bg-sky-50 text-sky-500 border-sky-200 hover:bg-sky-100' },
  'Maintenance':  { active: 'bg-yellow-100 text-yellow-700 border-yellow-300',        inactive: 'bg-yellow-50 text-yellow-600 border-yellow-200 hover:bg-yellow-100' },
  'Conditioning': { active: 'bg-emerald-100 text-emerald-700 border-emerald-300',     inactive: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100' },
  'Stats':        { active: 'bg-gray-100 text-gray-600 border-gray-300',              inactive: 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50' },
  'Travel':       { active: 'bg-gray-100 text-gray-600 border-gray-300',              inactive: 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50' },
  'Notes':        { active: 'bg-gray-100 text-gray-600 border-gray-300',              inactive: 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50' },
};

const PILL = {
  active:   'px-3 py-1 rounded-full text-xs font-medium border bg-primary text-white border-primary',
  inactive: 'px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-500 border-gray-200 hover:bg-gray-50',
};

function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function formatDate(d, t) {
  if (!d) return '—';
  const date = new Date(d + 'T12:00:00');
  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return t ? `${label} @ ${fmt12(t)}` : label;
}

export default function CareLogTable({
  entries, loading, onRowClick, onDeleted,
  // Global mode props
  globalMode = false,
  horses = [],
  locations = [],
  catalogItems = [],
  onBulkPublish,
  layupFilter = false,
  onLayupFilterClear,
}) {
  const [activeCategory, setActiveCategory]   = useState('All');
  const [publishedOnly, setPublishedOnly]     = useState(false);
  const [performedByName, setPerformedByName] = useState('');
  const [byType, setByType]                   = useState('All');
  const [descriptionSearch, setDescriptionSearch] = useState('');
  const [dateFrom, setDateFrom]               = useState('');
  const [dateTo, setDateTo]                   = useState('');
  // Global mode state
  const [filterHorseId, setFilterHorseId]       = useState('');
  const [filterLocationId, setFilterLocationId] = useState('');
  const [selectedIds, setSelectedIds]           = useState(new Set());
  const [filterBreeding, setFilterBreeding]     = useState(false);

  // Breeding filter: check catalog item's current is_breeding flag (source of truth)
  const breedingCatalogIds = new Set(
    (catalogItems || []).filter(c => c.is_breeding).map(c => c.id)
  );
  const isBreedingEntry = (e) =>
    e.care_catalog_item_id
      ? breedingCatalogIds.has(e.care_catalog_item_id)
      : !!e.is_breeding; // fallback for entries without a catalog link

  const filtered = (entries || [])
    .filter(e => activeCategory === 'All' || e.category === activeCategory)
    .filter(e => !publishedOnly || e.publish_to_owner_portal)
    .filter(e => !performedByName || (e.performed_by || '').toLowerCase().includes(performedByName.toLowerCase()))
    .filter(e => {
      if (byType === 'All') return true;
      if (byType === 'In-house') return e.performed_by_type === 'In-house';
      return e.performed_by_type !== 'In-house';
    })
    .filter(e => {
      if (!descriptionSearch) return true;
      const q = descriptionSearch.toLowerCase();
      return (e.name || '').toLowerCase().includes(q) || (e.type || '').toLowerCase().includes(q);
    })
    .filter(e => {
      if (!e.log_date) return !dateFrom && !dateTo;
      if (dateFrom && e.log_date < dateFrom) return false;
      if (dateTo && e.log_date > dateTo) return false;
      return true;
    })
    // Global mode additional filters
    .filter(e => !globalMode || !filterHorseId || e.horse_id === filterHorseId)
    .filter(e => !globalMode || !filterLocationId || e.location_id === filterLocationId)
    .filter(e => !filterBreeding || isBreedingEntry(e))
    .filter(e => !globalMode || !layupFilter || e.is_layup);

  const anyFilterActive = activeCategory !== 'All' || publishedOnly || performedByName
    || byType !== 'All' || descriptionSearch || dateFrom || dateTo || filterBreeding
    || (globalMode && (filterHorseId || filterLocationId || layupFilter));

  const allFilteredSelected = filtered.length > 0 && filtered.every(e => selectedIds.has(e.id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allFilteredSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map(e => e.id)));
  };

  const toggleSelectOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const handleExport = () => {
    const cols = globalMode
      ? ['Horse', 'Category', 'Type', 'Description', 'Dosage', 'Units', 'Date', 'Time', 'Location', 'By', 'Comments']
      : ['Category', 'Type', 'Description', 'Dosage', 'Units', 'Date', 'Time', 'Location', 'By', 'Comments'];
    const rows = [
      cols,
      ...filtered.map(e => {
        const base = [
          e.category, e.type,
          e.name, e.dosage ? `${e.dosage} ${e.dosage_unit || ''}`.trim() : '',
          e.units ?? '',
          e.log_date, e.log_time || '',
          e.location_name || '',
          e.performed_by ? `${e.performed_by} (${e.performed_by_type})` : '',
          e.comments || '',
        ];
        return globalMode ? [e.horse_name || '', ...base] : base;
      }),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'care_log.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-2">
      {/* Row 1 — Category pills + Breeding + Bulk actions + Export */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap gap-1.5">
          {['All', 'Medical', 'Daily Care', 'Maintenance', 'Conditioning'].map(c => (
            <button key={c} onClick={() => setActiveCategory(c)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                activeCategory === c
                  ? (FILTER_STYLES[c]?.active  || FILTER_STYLES['All'].active)
                  : (FILTER_STYLES[c]?.inactive || FILTER_STYLES['All'].inactive)
              }`}>{c}</button>
          ))}
          <button
            onClick={() => setFilterBreeding(v => !v)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              filterBreeding
                ? 'bg-pink-100 text-pink-700 border-pink-300'
                : 'bg-pink-50 text-pink-400 border-pink-200 hover:bg-pink-100'
            }`}
            data-testid="care-log-filter-breeding">
            Breeding
          </button>
          {['Stats', 'Travel', 'Notes'].map(c => (
            <button key={c} onClick={() => setActiveCategory(c)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                activeCategory === c
                  ? (FILTER_STYLES[c]?.active  || FILTER_STYLES['All'].active)
                  : (FILTER_STYLES[c]?.inactive || FILTER_STYLES['All'].inactive)
              }`}>{c}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {globalMode && layupFilter && (
            <button
              onClick={onLayupFilterClear}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
              data-testid="care-log-clear-layup-filter">
              <X className="w-3 h-3" /> Layup Only
            </button>
          )}
          {globalMode && someSelected && onBulkPublish && (
            <>
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => { onBulkPublish([...selectedIds], true); setSelectedIds(new Set()); }}
                data-testid="care-log-bulk-publish">
                <Globe className="w-3 h-3 mr-1" /> Publish ({selectedIds.size})
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => { onBulkPublish([...selectedIds], false); setSelectedIds(new Set()); }}
                data-testid="care-log-bulk-unpublish">
                <X className="w-3 h-3 mr-1" /> Unpublish ({selectedIds.size})
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={handleExport} className="shrink-0">
            <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-center py-8 text-muted-foreground">Loading care log…</p>
      ) : filtered.length === 0 && !anyFilterActive ? (
        <p className="text-sm text-center py-8 text-muted-foreground italic">No care logged yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* Row 1 — column labels */}
              <TableRow>
                {globalMode && (
                  <TableHead className="text-xs w-8 py-2">
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll}
                      data-testid="care-log-select-all" />
                  </TableHead>
                )}
                {globalMode && <TableHead className="text-xs">Horse</TableHead>}
                <TableHead className="text-xs">Category</TableHead>
                <TableHead className="text-xs">Description</TableHead>
                <TableHead className="text-xs">Quantity</TableHead>
                <TableHead className="text-xs">Date / Time</TableHead>
                <TableHead className="text-xs">By</TableHead>
                <TableHead className="text-xs">Location</TableHead>
                <TableHead className="text-xs">Comments</TableHead>
              </TableRow>
              {/* Row 2 — column-aligned filters */}
              <TableRow className="bg-muted/10 hover:bg-muted/10">
                {globalMode && <TableHead className="py-1.5" />}
                {globalMode && (
                  <TableHead className="py-1.5">
                    <Select value={filterHorseId || '__all__'} onValueChange={v => setFilterHorseId(v === '__all__' ? '' : v)}>
                      <SelectTrigger className="h-5 text-[10px] w-full px-1.5 min-w-[100px]" data-testid="care-log-filter-horse">
                        <SelectValue placeholder="All horses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All horses</SelectItem>
                        {horses.map(h => <SelectItem key={h.id} value={h.id}>{h.system_name || h.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableHead>
                )}
                <TableHead className="py-1.5" />
                <TableHead className="py-1.5">
                  <Input
                    value={descriptionSearch}
                    onChange={e => setDescriptionSearch(e.target.value)}
                    placeholder="Search…"
                    data-testid="care-log-description-search"
                    className="h-5 text-[10px] w-full px-1.5" />
                </TableHead>
                <TableHead className="py-1.5" />
                <TableHead className="py-1.5">
                  <div className="flex items-center gap-1">
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={e => setDateFrom(e.target.value)}
                      data-testid="care-log-date-from"
                      className={`h-5 text-[10px] w-[105px] px-1 ${dateFrom ? 'time-filled' : ''}`} />
                    <span className="text-[10px] text-muted-foreground">–</span>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={e => setDateTo(e.target.value)}
                      data-testid="care-log-date-to"
                      className={`h-5 text-[10px] w-[105px] px-1 ${dateTo ? 'time-filled' : ''}`} />
                    {(dateFrom || dateTo) && (
                      <button
                        onClick={() => { setDateFrom(''); setDateTo(''); }}
                        data-testid="care-log-date-clear"
                        className="text-[10px] text-muted-foreground hover:text-foreground px-1"
                        title="Clear date range">×</button>
                    )}
                  </div>
                </TableHead>
                <TableHead className="py-1.5">
                  <div className="flex flex-wrap items-center gap-1">
                    <select
                      value={byType}
                      onChange={e => setByType(e.target.value)}
                      className="h-5 text-[10px] border rounded px-1 bg-white"
                    >
                      <option value="All">All</option>
                      <option value="In-house">In-house</option>
                      <option value="Other">Other</option>
                    </select>
                    <Input value={performedByName} onChange={e => setPerformedByName(e.target.value)}
                      placeholder="Name…" className="h-5 text-[10px] w-20 px-1.5" />
                  </div>
                </TableHead>
                {globalMode ? (
                  <TableHead className="py-1.5">
                    <Select value={filterLocationId || '__all__'} onValueChange={v => setFilterLocationId(v === '__all__' ? '' : v)}>
                      <SelectTrigger className="h-5 text-[10px] w-full px-1.5 min-w-[80px]" data-testid="care-log-filter-location">
                        <SelectValue placeholder="All locations" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All locations</SelectItem>
                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableHead>
                ) : (
                  <TableHead className="py-1.5" />
                )}
                <TableHead className="py-1.5">
                  <button onClick={() => setPublishedOnly(v => !v)}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${publishedOnly ? PILL.active : PILL.inactive}`}>
                    <Globe className="w-2.5 h-2.5 inline mr-0.5" />Published
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(entry => (
                <TableRow key={entry.id} className="text-xs cursor-pointer hover:bg-muted/30"
                  onClick={() => onRowClick && onRowClick(entry)}>
                  {globalMode && (
                    <TableCell className="text-xs w-8 py-2" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(entry.id)}
                        onChange={() => toggleSelectOne(entry.id)}
                        data-testid={`care-log-check-${entry.id}`} />
                    </TableCell>
                  )}
                  {globalMode && (
                    <TableCell className="text-xs font-medium whitespace-nowrap">
                      {entry.horse_name || '—'}
                    </TableCell>
                  )}
                  <TableCell className="text-xs">{entry.category}</TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {entry.category === 'Stats'
                        ? entry.name
                        : (entry.name || '').toLowerCase().startsWith((entry.type || '').toLowerCase())
                          ? entry.name
                          : `${entry.type} — ${entry.name}`}
                      {entry.dosage
                        ? ` (${entry.dosage}${entry.dosage_unit ? ' ' + entry.dosage_unit : ''})`
                        : entry.serving_size != null
                          ? ` (${entry.serving_size}${entry.serving_size_units ? ' ' + entry.serving_size_units : ''})`
                          : ''}
                    </span>
                  </TableCell>
                  <TableCell>{entry.units ?? '—'}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {entry.is_layup && entry.end_date
                      ? `${formatDate(entry.log_date)} → ${formatDate(entry.end_date)}`
                      : entry.is_layup
                        ? `${formatDate(entry.log_date)} → Open-ended`
                        : formatDate(entry.log_date, entry.log_time)}
                  </TableCell>
                  <TableCell>
                    {entry.performed_by ? (
                      <div>
                        <div>{entry.performed_by}</div>
                        <div className="text-muted-foreground text-[10px]">{entry.performed_by_type}</div>
                      </div>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{entry.location_name || '—'}</TableCell>
                  <TableCell className="max-w-[120px] truncate">
                    {entry.publish_to_owner_portal && <Globe className="w-3 h-3 text-blue-500 inline mr-1" title="Published" />}
                    {entry.comments || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length === 0 && anyFilterActive && (
            <p className="text-sm text-center py-6 text-muted-foreground italic">No entries match the current filters.</p>
          )}
        </div>
      )}
    </div>
  );
}
